import { bopApi } from '@/lib/api/client';

// mm-program jobs: the schedule's runs and reports, backfills and reports sent again, queued in mm-program's jobs table.
// Contract: mm-program docs/jobs-contract.md.
const JOBS_PATH = 'v1/mm-programs/jobs';

export type JobType = 'scheduled' | 'score' | 'daily_report' | 'monthly_report';
export type JobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
export type EmailMode = 'skip' | 'default' | 'override';
/** Reports a backfill rebuilds and sends after its runs */
export type JobReport = 'daily' | 'monthly';

export interface JobEmail {
  mode: EmailMode;
  to?: string;
  bcc?: string[];
}

/** What BOP sends; bop-api adds `requested_by` from the sign-in. */
export interface JobRequest {
  type: Exclude<JobType, 'scheduled'>;
  periods?: { from: string; to: string }[];
  date?: string;
  month?: string;
  /** Monthly reports only: the first UTC day reported, `YYYY-MM-DD` in `month`; runs before it are neither required nor reported */
  from?: string;
  /** Backfills only; without, the job only scores and takes no email */
  reports?: JobReport[];
  email?: JobEmail;
  market_makers?: string[];
  resend?: boolean;
  justification: string;
}

export interface Job {
  id: string;
  type: JobType;
  status: JobStatus;
  created_at: string;
  requested_by: string;
  justification?: string;
  runs?: string[];
  date?: string;
  month?: string;
  /** A monthly report from this UTC day on, rather than the whole month */
  from?: string;
  reports?: JobReport[];
  /** How a job that sends reports sends them; absent on one that only scores */
  email?: JobEmail;
  market_makers?: string[];
  resend?: boolean;
  cancel_requested?: boolean;
  started_at?: string;
  finished_at?: string;
  heartbeat_at?: string;
  /** Steps: a score job's runs and then its reports, or 1 for a report job */
  total?: number;
  done?: number;
  /** The step in progress, or the one that failed: a run start, day or month */
  current?: string;
  error?: string;
  log?: string;
}

export interface WorkerHeartbeat {
  state: 'starting' | 'idle' | 'busy' | 'held';
  heartbeat_at: string;
  job?: string;
}

export interface JobPlan {
  type: JobType;
  runs: string[];
  reportDays: string[];
  reportMonths: string[];
  /** A monthly report's first UTC day, `YYYY-MM-DD`; null for the whole month or another job type */
  reportFrom: string | null;
  /** Days whose 18:00 run is rescored without the day's other runs */
  partialDays: string[];
  marketMakers: { p: string; name: string }[];
  /** A backfill scores every active market maker, whoever gets reports */
  scoresEveryone: boolean;
  /** Null for a backfill that sends no reports */
  email: JobEmail | null;
  dailyRecipients: { name: string; to: string; bcc: string[] }[];
  /** `to: "monthly recipients"` is the environment's; null when no monthly report is sent */
  monthly: { to: string; bcc: string[] | null } | null;
  resend: boolean;
  emailsMarketMakers: boolean;
}

export interface ListJobsResponse {
  jobs: Job[];
  worker: WorkerHeartbeat | null;
  canSubmit: boolean;
}

export interface PlanJobResponse {
  plan: JobPlan;
  job: Job;
}

export interface CreateJobResponse {
  job: Job;
  plan: JobPlan;
  auditWritten: boolean;
}

export interface CancelJobResponse {
  outcome: 'cancelled' | 'cancel_requested' | JobStatus;
  job: Job | null;
  auditWritten: boolean;
}

export const listMmProgramJobs = () => bopApi.get(JOBS_PATH).json<ListJobsResponse>();

export const planMmProgramJob = (request: JobRequest) => bopApi.post(JOBS_PATH, { json: request, searchParams: { dry_run: 'true' } }).json<PlanJobResponse>();

export const createMmProgramJob = (request: JobRequest) => bopApi.post(JOBS_PATH, { json: request }).json<CreateJobResponse>();

export const cancelMmProgramJob = (id: string) => bopApi.post(`${JOBS_PATH}/${encodeURIComponent(id)}/cancel`).json<CancelJobResponse>();
